import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { DebugCaptureResult } from '../components/common/DebugRequestPanel';

interface DebugInfoContextValue {
  /** Debug info from the last save operation */
  lastSaveDebug: DebugCaptureResult | null;
  /** Debug info from the last publish operation */
  lastPublishDebug: DebugCaptureResult | null;
  /** Set debug info from a save operation */
  setLastSaveDebug: (debug: DebugCaptureResult | null) => void;
  /** Set debug info from a publish operation */
  setLastPublishDebug: (debug: DebugCaptureResult | null) => void;
  /** Clear all debug info */
  clearDebugInfo: () => void;
  /** Check if there's any debug info available */
  hasDebugInfo: boolean;
}

const DebugInfoContext = createContext<DebugInfoContextValue | undefined>(
  undefined,
);

interface DebugInfoProviderProps {
  children: ReactNode;
}

/**
 * Provider for storing debug information from save/publish operations
 */
export const DebugInfoProvider: React.FC<DebugInfoProviderProps> = ({
  children,
}) => {
  const [lastSaveDebug, setLastSaveDebugState] =
    useState<DebugCaptureResult | null>(null);
  const [lastPublishDebug, setLastPublishDebugState] =
    useState<DebugCaptureResult | null>(null);

  const setLastSaveDebug = useCallback((debug: DebugCaptureResult | null) => {
    setLastSaveDebugState(debug);
  }, []);

  const setLastPublishDebug = useCallback(
    (debug: DebugCaptureResult | null) => {
      setLastPublishDebugState(debug);
    },
    [],
  );

  const clearDebugInfo = useCallback(() => {
    setLastSaveDebugState(null);
    setLastPublishDebugState(null);
  }, []);

  const hasDebugInfo = lastSaveDebug !== null || lastPublishDebug !== null;

  return (
    <DebugInfoContext.Provider
      value={{
        lastSaveDebug,
        lastPublishDebug,
        setLastSaveDebug,
        setLastPublishDebug,
        clearDebugInfo,
        hasDebugInfo,
      }}
    >
      {children}
    </DebugInfoContext.Provider>
  );
};

/**
 * Hook to access debug info context
 */
export function useDebugInfo(): DebugInfoContextValue {
  const context = useContext(DebugInfoContext);
  if (context === undefined) {
    throw new Error('useDebugInfo must be used within a DebugInfoProvider');
  }
  return context;
}

/**
 * Optional hook that returns undefined if not in provider
 */
export function useDebugInfoOptional(): DebugInfoContextValue | undefined {
  return useContext(DebugInfoContext);
}

export default DebugInfoContext;
